import { useState, useEffect, useCallback, useRef } from 'react';
import { useIndexedDB } from '@thd-olt-global/thd-storage-utils';
import { getUserName } from '../utils/assistant-context-utils';
import { getDefaultSession, getSessionId, sessionIsExpired } from '../utils/session-utils';
import { useGenerateQuestions } from './useGenerateQuestions';

const getInitialMessage = () => {
  const authUser = getUserName();
  return {
    type: 'bot',
    content: {
      responseSummary: {
        // eslint-disable-next-line max-len
        summaryAnswer: `Hi${authUser ? `  ${authUser}` : ''}! Need help with a product, project, or question? I'm here to assist. What can I help you with today?`
      },
      suggestedQuestions: [],
    },
    sessionId: getSessionId(),
    // Set to true since no feedback prompt should be shown for initial message
    feedbackProvided: true,
    timestamp: Date.now(),
    isInitialMessage: true,
  };
};

/**
 * @param { boolean } withPersistence - Flag to check if persistence is desired
 * @returns { UseMessageHistoryReturn }
 */
export const useMessageHistory = (withPersistence = true) => {
  const session = useRef(null);
  const maBroadcastChannel = useRef(null);
  const isStorageAvailable = useRef(false);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  /* eslint-disable camelcase */
  const {
    getByKey: iDBGetByKey_session,
    getAll: iDBGetAll_session,
    add: iDBAdd_session,
    cursor: iDBCursor_session,
  } = useIndexedDB('sessions');
  const {
    getAll: iDBGetAll_messages,
    add: iDBAdd_message,
    update: iDBUpdate_message,
    clear: iDBClear_messages,
    cursor: iDBCursor_message,
  } = useIndexedDB('messages');
  /* eslint-enable camelcase */

  const [submitToGenerateQuestions, {
    data: suggestedQuestionsResponse,
    loading: isSuggestedQuestionsLoading,
    error: suggestedQuestionsError,
  }] = useGenerateQuestions();

  const loadIDBMessages = () => {
    setIsLoading(true);
    return iDBGetAll_messages()
      .then((savedMessages) => {
        const sortedMessages = [...savedMessages].sort((first, second) => {
          if (first.isInitialMessage) return -1;
          if (second.isInitialMessage) return 1;
          return 0;
        });
        setMessages(sortedMessages);
        setIsLoading(false);
        return savedMessages;
      });
  };

  const handleSaveMessage = async (newMessage) => {
    const updatedMessage = {
      ...newMessage,
      sessionId: session.current.sessionId,
      feedbackProvided: false,
      timestamp: Date.now(),
      isInitialMessage: false,
    };
    if (!isStorageAvailable.current) {
      setMessages([...messages, updatedMessage]);
      return Promise.resolve(updatedMessage);
    }
    if (!session.current.sessionId) return Promise.reject(new Error('Session ID not available'));

    return iDBAdd_message(updatedMessage)
      .then(loadIDBMessages)
      .then((savedMessages) => savedMessages.at(-1));
  };

  const clearHistory = useCallback(async ({ origin }) => {
    // Reuse existing message to avoid refetch of initial suggested questions
    // Not sure conditional is reachable, but is there to be safe for now
    const initialMessage = messages.find(({ isInitialMessage }) => isInitialMessage) || getInitialMessage();
    // message table is constrained to unique ids
    delete initialMessage.id;
    // Helps mitigate multiple tabs clearing and resetting the db (there is still a conflict if same tab is open
    // This should be updated so that the loader communicates through the event bus
    // Then a broadcast channel goes out to the other tabs to reload the db
    if (origin === window.top.location.origin) {
      await iDBClear_messages()
        .then(() => iDBAdd_message(initialMessage));
    }

    await loadIDBMessages();
  }, [messages]);

  const setFeedbackProvided = async (message) => {
    const updatedMessage = JSON.parse(JSON.stringify(message));
    updatedMessage.feedbackProvided = true;

    await iDBUpdate_message(updatedMessage);
  };

  const broadCastMessageUpdate = () => {
    if (maBroadcastChannel.current) {
      maBroadcastChannel.current.postMessage('magic-apron-new-message');
    }
  };

  const cleanIndexDb = async () => {
    // Remove expired sessions
    await iDBCursor_session((event) => {
      const cursor = event.target.result;

      if (cursor) {
        if (!cursor.value.sessionId || sessionIsExpired(cursor.value)) {
          cursor.delete();
        }
        cursor.continue();
      }
    });

    // Remove messages that have no session associated in db
    const retrievedSessions = await iDBGetAll_session();
    const retrievedSessionIds = retrievedSessions.map(({ sessionId }) => sessionId);

    await iDBCursor_message((event) => {
      const cursor = event.target.result;

      if (cursor) {
        if (!retrievedSessionIds.includes(cursor.value.sessionId)) {
          cursor.delete();
        }
        cursor.continue();
      }
    });
  };

  const init = async () => {
    const savedSession = await iDBGetByKey_session(getSessionId());
    let activeSession = savedSession;

    if (!activeSession) {
      const newSession = getDefaultSession();
      await iDBAdd_session(newSession);
      activeSession = newSession;
    }
    session.current = activeSession;
    isStorageAvailable.current = withPersistence && typeof indexedDB !== 'undefined';
    await cleanIndexDb();

    // We were not saving initial question in db before
    // So it is possible that an existing session would have no messages to display
    // Or it has messages but no initialMessage
    // The savedMessages.find condition can be removed once this code has sat in prod for a little bit
    const savedMessages = await iDBGetAll_messages();
    if (!savedSession || !savedMessages.find(({ isInitialMessage }) => isInitialMessage)) {
      await iDBAdd_message(getInitialMessage())
        .then(submitToGenerateQuestions);
    }

    await loadIDBMessages();
  };

  useEffect(() => {
    if (typeof BroadcastChannel !== 'undefined') {
      maBroadcastChannel.current = new BroadcastChannel('magic-apron-new-message');
    }

    init()
      .then(() => {
        if (maBroadcastChannel.current) {
          maBroadcastChannel.current.onmessage = loadIDBMessages;
        }
      })
      .catch(() => {});

    return (() => {
      if (maBroadcastChannel.current) {
        maBroadcastChannel.current.close();
      }
    });
  }, []);

  useEffect(() => {
    async function commitInitialSuggestedQuestions() {
      if (!isSuggestedQuestionsLoading && !suggestedQuestionsError && suggestedQuestionsResponse) {
        const dbMessages = await iDBGetAll_messages();
        const initialMessage = dbMessages.find(({ isInitialMessage }) => isInitialMessage);

        const generatedSuggestedQuestions = suggestedQuestionsResponse?.generateQuestions?.suggestedQuestions || [];
        initialMessage.content.suggestedQuestions = generatedSuggestedQuestions;

        await iDBUpdate_message(initialMessage)
          .then(loadIDBMessages)
          .finally(() => { broadCastMessageUpdate(); });
      }
    }

    commitInitialSuggestedQuestions().catch(() => {});
  }, [isSuggestedQuestionsLoading, suggestedQuestionsResponse]);

  return {
    messages,
    saveMessage: handleSaveMessage,
    historyLoading: isLoading,
    isSuggestedQuestionsLoading,
    clearHistory,
    setFeedbackProvided,
    broadCastMessageUpdate,
  };
};
